import '../../../css/modal.css';
import { createFetchTableData } from '../../../utils/createFetchTableData';
import { useState, useEffect } from "react";
import MonitorModal from './MonitorModal';
import { useCallback, useMemo } from "react";
import Datatable from "../../../components/ui/datatable/Datatable";

import { post, getUno } from '../../../api/apis';
import { mensajeEstatico, modalCargando } from '../../../components/ui/sweetAlerts';
import { BtnEnviarGenerarExcel } from '../../../components/BtnObtenerExcel';

const MonitorWebdox = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  // Configuración de orden inicial para el datatable
  const initialSortBy = 'IdLote';
  const initialSortDir = 'desc';

  // --- LÓGICA DE DATOS PARA DATATABLE ---
  // Se entrega el endpoint del cual se obtendrán los datos y donde se encuentran los datos dentro de la respuesta para armar la tabla
  const fetchTableData = useMemo(() =>
    createFetchTableData({
      url: 'monitor/contratos',
      initialSortBy, // columna por defecto
      initialSortDir, // dirección por defecto
      extraParams: { proveedor: 1 }, // Parámetro adicional para el backend
      dataPath: ['data', 'data', 'data'], // ruta dentro de la respuesta donde se encuentran los datos
      totalPath: ['data', 'data', 'total'] // ruta dentro de la respuesta donde se encuentra el total
    })
    , [initialSortBy, initialSortDir]);

  // --- LÓGICA DE REENVÍO DE CONTRATO ---
  const reenviarContrato = async (id) => {
    try {
      modalCargando('Reenviando');
      const resp = await post('reenvio_contrato', { id_contrato: id });
      if (resp.data.status === 'info') { mensajeEstatico('info', 'Atención', resp.data.message); }
      else if (resp.data.status === 'success') {
        mensajeEstatico('success', 'Éxito', resp.data.message);
        // Aquí podrías refrescar la tabla si lo necesitas
      }
      else { mensajeEstatico('error', 'Error', 'Ha ocurrido un error.'); }
    } catch (error) {
      console.error("Error al reenviar contrato:", error);
      mensajeEstatico('error', `No se pudo reenviar el contrato ${id}.`);
    }
  };

  const BtnReenvio = ({ dato, id }) => (
    dato === '1'
      ? (
        <button
          onClick={(e) => { e.stopPropagation(); reenviarContrato(id); }}
          className="btn"
          style={{
            fontSize: '11px',
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: '#1976d2',
            color: '#ffffff',
            border: 'none',
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            letterSpacing: '0.3px'
          }}
          onMouseEnter={e => { 
            e.currentTarget.style.backgroundColor = '#1565c0'; 
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(25, 118, 210, 0.4)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={e => { 
            e.currentTarget.style.backgroundColor = '#1976d2'; 
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(25, 118, 210, 0.3)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Reenviar
        </button>
      )
      : null
  );

  // Cerrar modal al presionar Escape o clic fuera del modal
  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowModal(false);
      }
    };
    const handleClickOutside = (e) => {
      // Si el modal está abierto y el clic no es dentro del contenido del modal
      const modalContent = document.querySelector('.modal-content');
      if (modalContent && !modalContent.contains(e.target)) {
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);


  // Manejar doble clic en una fila para abrir el modal
  const handleRowDoubleClick = (rowData) => {
    setSelectedRow(rowData);
    setShowModal(true);
  };

  // --- DEFINICIÓN DE COLUMNAS (MEMORIZADA) ---
  const columnsDef = useMemo(() => [
    { label: 'Id envío', sortable: true, searchForColumn: true, key: 'IdLote', selector: row => row.IdLote, headerAlign: 'left', dataAlign: 'left', width: '80px' },
    { label: 'N° Personal', sortable: true, searchForColumn: true, key: 'NPersonal', selector: row => row.NPersonal, headerAlign: 'left', dataAlign: 'left', width: '100px' },
    { label: 'Nombre colaborador', sortable: true, searchForColumn: true, key: 'Nombre', selector: row => row.Nombre, headerAlign: 'left', dataAlign: 'left', width: '200px' },
    { label: 'RUT', sortable: true, searchForColumn: true, key: 'Rut', selector: row => row.Rut, headerAlign: 'left', dataAlign: 'left', width: '120px' },
    { label: 'División', sortable: true, searchForColumn: true, key: 'Division', selector: row => row.Division, headerAlign: 'left', dataAlign: 'left', width: '150px' },
    { label: 'Unidad organizativa', sortable: true, searchForColumn: true, key: 'TextoUnidadOrganizativa', selector: row => row.TextoUnidadOrganizativa, headerAlign: 'left', dataAlign: 'left', width: '200px' },
    { label: 'Subdivisión', sortable: true, searchForColumn: true, key: 'Subdivision', selector: row => row.Subdivision, headerAlign: 'left', dataAlign: 'left', width: '150px' },
    { label: 'Cargo', sortable: true, searchForColumn: true, key: 'Cargo', selector: row => row.Cargo, headerAlign: 'left', dataAlign: 'left', width: '200px' },
    { label: 'Fecha ingreso', sortable: true, searchForColumn: true, key: 'FechaIngreso', selector: row => row.FechaIngreso && row.FechaIngreso.replace(/\./g, '-'), headerAlign: 'left', dataAlign: 'left', width: '120px' },
    { label: 'Emisión contrato', sortable: true, searchForColumn: true, key: 'FechaEmisionContrato', selector: row => row.FechaEmisionContrato && row.FechaEmisionContrato.replace(/\./g, '-'), headerAlign: 'left', dataAlign: 'left', width: '140px' },
    { label: 'Id interno Webdox', sortable: false, searchForColumn: false, key: 'id_wf', selector: row => row.id_wf ? row.id_wf : '---', headerAlign: 'left', dataAlign: 'left', width: '130px' },
    { label: '% Avance', sortable: false, searchForColumn: false, key: 'porcentaje_avance', selector: row => row.porcentaje_avance != null ? row.porcentaje_avance : 0, headerAlign: 'left', dataAlign: 'left', width: '90px' },
    { label: 'Recordatorio', sortable: true, searchForColumn: false, key: 'reenvio_contrato', selector: row => <BtnReenvio dato={row.reenvio_contrato} id={row.id} />, headerAlign: 'left', dataAlign: 'center', width: '120px' },
  ], []);


  return (
    <>
      {/* datatabla dinámico con los datos del monitor */}
      <Datatable
        storageKey="monitor-webdox-table"
        titletable=""
        requestDataFn={fetchTableData}
        initialPerPage={10}
        columns={columnsDef}
        orderColumns={true}
        adjustColumnSize={true}
        searchEnabled={true}
        searchColumnsEnabled={true}
        manualPageInput={true}
        onRowDoubleClick={handleRowDoubleClick}
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialSortBy={initialSortBy}
        initialSortDir={initialSortDir}
      />

      {/* Boton para descargar excel */}
      <div className="row mt-3">
        <div className="col-sm-12">
          <BtnEnviarGenerarExcel ubicacionArchivo="excelMonitor/ReporteDataMonitorWebdox.xlsx" agregarFecha={true} />
        </div>
      </div>

      {/* Modal de detalles del registro */}
      <MonitorModal show={showModal} row={selectedRow} onClose={() => setShowModal(false)} />
    </>
  );
};

export default MonitorWebdox;